import PropTypes from "prop-types"

/**
 * Selection state is a simple array of selected IDs.
 * This replaces the previous { mode: "include", ids: [...] } format.
 */

export const SelectionPropType = PropTypes.arrayOf(
  PropTypes.oneOfType([PropTypes.string, PropTypes.number])
)

export const SelectablePropType = PropTypes.shape({
  selection: SelectionPropType.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  isRowSelectable: PropTypes.func,
  totalSelectableCount: PropTypes.number,
  allSelectableIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      variant: PropTypes.string,
    })
  ),
})

/**
 * Creates an empty selection state
 */
export const emptySelection = () => []

/**
 * Checks if a specific row is selected
 */
export const isRowSelected = (selection, rowId) => selection.includes(rowId)

/**
 * Gets the count of selected items
 */
export const getSelectedCount = (selection) => selection.length

/**
 * Toggles a row's selection state
 */
export const toggleRowSelection = (selection, rowId) => {
  return selection.includes(rowId)
    ? selection.filter((id) => id !== rowId)
    : [...selection, rowId]
}

/**
 * Selects specific row IDs (used for "Select all" on current page)
 */
export const selectRows = (selection, rowIds) => {
  return [...new Set([...selection, ...rowIds])]
}

/**
 * Selects all provided IDs
 */
export const selectAll = (ids) => ids ?? []

/**
 * Deselects all items
 */
export const deselectAll = () => []

/**
 * Checks if any rows are selected
 */
export const hasSelection = (selection) => selection.length > 0

/**
 * Checks if all selectable rows on the current page are selected
 */
export const isAllPageRowsSelected = (selection, pageSelectableIds) => {
  if (pageSelectableIds.length === 0) return false
  const selectionSet = new Set(selection)
  return pageSelectableIds.every((id) => selectionSet.has(id))
}

/**
 * Checks if all selectable items across all pages are selected
 */
export const isAllSelected = (selection, allSelectableIds) => {
  if (!allSelectableIds || allSelectableIds.length === 0) return false
  const selectionSet = new Set(selection)
  return allSelectableIds.every((id) => selectionSet.has(id))
}
